import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

// Global event bus for triggering upgrade modal from anywhere (e.g. api interceptor)
export const upgradeEvents = {
  listeners: [],
  emit(data) { this.listeners.forEach(fn => fn(data)); },
  on(fn) { this.listeners.push(fn); },
  off(fn) { this.listeners = this.listeners.filter(l => l !== fn); }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const PLAN_HIERARCHY = ['starter', 'professional', 'enterprise'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

  const loadUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
    } catch (error) {
      console.error('Load user error:', error);
      if (error.response && error.response.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const googleLogin = async (googleToken) => {
    try {
      const response = await api.post('/auth/google-login', { token: googleToken });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Google Login failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  /** Returns true if the user's plan meets or exceeds the required plan */
  const canAccess = (requiredPlan) => {
    const userPlan = user?.planType || 'starter';
    const userIdx = PLAN_HIERARCHY.indexOf(userPlan);
    const reqIdx = PLAN_HIERARCHY.indexOf(requiredPlan);
    return userIdx >= reqIdx;
  };

  const value = {
    user,
    loading,
    login,
    googleLogin,
    logout,
    isAdmin,
    canAccess,
    token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
